import React, { FunctionComponent, useState } from 'react';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/lab';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextFieldProps,
} from '@mui/material';
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { v1 as uuidv1 } from 'uuid';

import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../components/V2/Buttons';
import {
  groupTutorRateVersionConditionTypeOptions,
  TGroupTutorRateVersionConditionTypeOption,
  groupTutorRateVersionTutorRegistrationDateConditionTypeOptions,
  TGroupTutorRateVersionTutorRegistrationDateConditionTypeOption,
} from '../../../../../../../constants';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';
import { capitalizeFirstLetter } from '../../../../../../../helper';
import { GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES } from '../../../../../../../store/V2/GroupTutorRateVersionsSlice';
import { ICustomCondition } from './AddGroupTutorRateVersionModal';

interface AddGroupTutorRateVersionConditionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (condition: ICustomCondition) => void;
}

type TState = {
  selectedConditionType: TGroupTutorRateVersionConditionTypeOption | null;
  selectedTutorRegistrationDateConditionType: TGroupTutorRateVersionTutorRegistrationDateConditionTypeOption | null;
  selectedTutorRegistrationDateConditionDate: Moment | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedConditionType: null,
  selectedTutorRegistrationDateConditionType: null,
  selectedTutorRegistrationDateConditionDate: null,
};

const AddGroupTutorRateVersionConditionModal: FunctionComponent<AddGroupTutorRateVersionConditionModalProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  // State
  const [state, setState] = useState({ ...initialState });

  const modalTitle = 'Add New Rate Condition';
  const isSubmitDisabled =
    !state.selectedConditionType ||
    (state.selectedConditionType &&
      (!state.selectedTutorRegistrationDateConditionType ||
        !state.selectedTutorRegistrationDateConditionDate));

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = () => {
    if (state.selectedConditionType) {
      if (
        state.selectedConditionType.value ===
          GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES.TUTOR_REGISTRATION_DATE &&
        state.selectedTutorRegistrationDateConditionType &&
        state.selectedTutorRegistrationDateConditionDate
      ) {
        onSubmit({
          _id: uuidv1(),
          type:
            GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES.TUTOR_REGISTRATION_DATE,
          logic: state.selectedTutorRegistrationDateConditionType.value,
          date: state.selectedTutorRegistrationDateConditionDate.toISOString(),
        });

        onClose();
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div style={{ paddingTop: '6px' }}>
            <Grid container spacing="20px" alignItems="center">
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.selectedConditionType}
                    options={groupTutorRateVersionConditionTypeOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    getOptionKey={(option) => option.value}
                    onChange={(event, value) =>
                      updateStateAttributes({ selectedConditionType: value })
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    getOptionLabel={(option) =>
                      capitalizeFirstLetter(
                        option.label.replaceAll('_', ' ').toLocaleLowerCase()
                      )
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="Condition Type *"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {state.selectedConditionType &&
              state.selectedConditionType.value ===
                GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES.TUTOR_REGISTRATION_DATE ? (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <Autocomplete
                        fullWidth
                        size="small"
                        value={state.selectedTutorRegistrationDateConditionType}
                        options={
                          groupTutorRateVersionTutorRegistrationDateConditionTypeOptions
                        }
                        popupIcon={TPAutoCompleteArrowIconComponent}
                        getOptionKey={(option) => option.value}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            selectedTutorRegistrationDateConditionType: value,
                          })
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.label === value.value
                        }
                        getOptionLabel={(option) =>
                          capitalizeFirstLetter(
                            option.label
                              .replaceAll('_', ' ')
                              .toLocaleLowerCase()
                          )
                        }
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...textFieldProps}
                            size="small"
                            label="Condition Logic *"
                            placeholder="Select"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <MobileDateTimePicker
                        clearable
                        value={state.selectedTutorRegistrationDateConditionDate}
                        label="Registration Date *"
                        inputFormat="YYYY-MM-DD hh:mm A"
                        onChange={(value) =>
                          updateStateAttributes({
                            selectedTutorRegistrationDateConditionDate: value,
                          })
                        }
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...textFieldProps}
                            size="small"
                            placeholder="Select"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <DateRangeIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </div>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkButton
          disableElevation
          variant="contained"
          color="inherit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ ...defaultButtonStyles }}
        >
          Add
        </DarkButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddGroupTutorRateVersionConditionModal;
